import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import type { Project } from '../types';

export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export interface UIState {
  // Sidebar state
  sidebarOpen: boolean;
  
  // Theme state
  theme: 'light' | 'dark' | 'system';
  
  // Workspace state
  workspaceId: string;
  
  // Toast notifications
  toastQueue: Toast[];
  
  // Loading states
  globalLoading: boolean;
  
  // Modal states
  activeModal: string | null;
  
  // Project selection state
  selectedProject: Project | null;
  
  // Actions
  setSidebarOpen: (open: boolean) => void;
  toggleSidebar: () => void;
  
  /** Pinned to Light; the argument is accepted for call-site compatibility and ignored. */
  setTheme: (theme?: 'light' | 'dark' | 'system') => void;
  
  setWorkspaceId: (id: string) => void;
  
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
  
  setGlobalLoading: (loading: boolean) => void;
  
  setActiveModal: (modalId: string | null) => void;
  
  // Project selection actions
  selectProject: (project: Project | null) => void;
  clearSelectedProject: () => void;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      sidebarOpen: true,
      theme: 'system',
      workspaceId: 'ws-1',
      toastQueue: [],
      globalLoading: false,
      activeModal: null,
      selectedProject: null,

      // Actions
      setSidebarOpen: (open: boolean) => {
        set({ sidebarOpen: open });
      },

      toggleSidebar: () => {
        set((state) => ({ sidebarOpen: !state.sidebarOpen }));
      },

      /**
       * The app is pinned to Light until dark mode is complete across every page
       * (founder ruling). Whatever is requested - a stale persisted value, a saved
       * server preference, the OS setting - the document renders Light.
       */
      setTheme: () => {
        set({ theme: 'light' });
        document.documentElement.classList.remove('dark');
      },

      setWorkspaceId: (id: string) => {
        set({ workspaceId: id });
      },

      addToast: (toast: Omit<Toast, 'id'>) => {
        const id = `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const newToast: Toast = {
          id,
          duration: 5000,
          ...toast,
        };

        set((state) => ({
          toastQueue: [...state.toastQueue, newToast],
        }));

        // Auto-remove toast after duration
        if (newToast.duration && newToast.duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, newToast.duration);
        }
      },

      removeToast: (id: string) => {
        set((state) => ({
          toastQueue: state.toastQueue.filter((toast) => toast.id !== id),
        }));
      },

      clearToasts: () => {
        set({ toastQueue: [] });
      },

      setGlobalLoading: (loading: boolean) => {
        set({ globalLoading: loading });
      },

      setActiveModal: (modalId: string | null) => {
        set({ activeModal: modalId });
      },

      selectProject: (project: Project | null) => {
        set({ selectedProject: project });
      },

      clearSelectedProject: () => {
        set({ selectedProject: null });
      },
    }),
    {
      name: 'zephix-ui-storage',
      partialize: (state) => ({
        sidebarOpen: state.sidebarOpen,
        theme: state.theme,
        workspaceId: state.workspaceId,
      }),
      /**
       * Do not call setTheme() from here. Persisted theme is applied by UserThemeSync
       * (or signed-out layout) so server preferences can override localStorage without a flash.
       */
    }
  )
);

// 🔧 Compat selectors (shim names some components expect):
export const selectIsSidebarOpen = (s: UIState) => s.sidebarOpen;
export const selectOpenSidebar   = (s: UIState) => s.setSidebarOpen;
export const selectCloseSidebar  = (s: UIState) => s.setSidebarOpen;